import type { SegmentFailureReason } from '@/modules/projects/stores'
import { humanizeText } from '@/utils'

// Turns the raw snake_case keys the engine stores (credit_card, mutual_fund) into the words we show,
// so the donut, holdings table, filters, and reconciliation card all read the same. lukita already
// humanizes transaction account types but the reconciliation and securities sources do not. Anything
// not in a map falls back to humanizeText, so a new value never shows as raw snake_case.

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  loan: 'Loan',
  investment: 'Investment',
  retirement: 'Retirement',
  other: 'Other',
  // Segment kinds (Statement::Segment enum) also flow through here for the Files tab badges.
  bank: 'Bank',
  mixed: 'Mixed',
  checks: 'Checks',
  unclassified: 'Unclassified',
  // A document the engine judged is not a bank statement at all (an invoice, say). It is kept and
  // shown, but no transactions were loaded from it.
  not_a_statement: 'Not a bank statement',
}

// Asset classes are free-form (whatever the statement printed), so this only overrides the few whose
// plain humanization reads wrong: acronyms (ETF) and the plural/two-word finance terms.
const ASSET_CLASS_LABELS: Record<string, string> = {
  equity: 'Equities',
  equities: 'Equities',
  stock: 'Equities',
  stocks: 'Equities',
  fixed_income: 'Fixed Income',
  'fixed income': 'Fixed Income',
  bond: 'Bonds',
  bonds: 'Bonds',
  cash: 'Cash',
  mutual_fund: 'Mutual Funds',
  'mutual fund': 'Mutual Funds',
  mutual_funds: 'Mutual Funds',
  etf: 'ETFs',
  etfs: 'ETFs',
  option: 'Options',
  options: 'Options',
  money_market: 'Money Market',
  'money market': 'Money Market',
  alternative: 'Alternatives',
  alternatives: 'Alternatives',
  unclassified: 'Unclassified',
}

// Securities activity types the engine emits in the Activity table's "Type" column. Plain
// brokerage-statement words: a buy is a "Buy", a fee is a "Fee", not 'security_purchase'/'other_fee'.
const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  security_purchase: 'Buy',
  security_sale: 'Sell',
  dividend: 'Dividend',
  reinvestment: 'Reinvested',
  interest: 'Interest',
  advisory_fee: 'Advisory fee',
  other_fee: 'Fee',
  other: 'Other',
}

// The engine tags each set-aside page range with the kind of document it is. Plain words the user
// reads next to an excluded range; a new class falls back to humanizeText.
const DOCUMENT_CLASS_LABELS: Record<string, string> = {
  correspondence: 'Correspondence',
  legal_filing: 'Legal filing',
  narrative_doc: 'Document',
  general_ledger: 'General ledger',
  tax_document: 'Tax document',
  invoice_receipt: 'Invoice / receipt',
  blank: 'Blank page',
}

// Plain-English one-liners for the segment failure-reason constants lukita stores
// (Statement::Segment SEGMENTATION/EXTRACT/ENRICHMENT_FAILURE_REASONS). Anything unmapped falls back
// to humanizeText so a new constant still reads sensibly instead of raw SCREAMING_CASE.
const FAILURE_REASON_LABELS: Record<string, string> = {
  SEGMENTATION_REQUEST_FAILED: 'Could not reach the segmenter for these pages',
  SEGMENTATION_FAILED: 'Could not split these pages into statements',
  SEGMENT_EXTRACT_REQUEST_FAILED:
    'Could not reach the extractor for these pages',
  SEGMENT_EXPORT_EXTRACTED_FAILED: 'Could not save the extracted rows',
  SEGMENT_ENRICHMENT_FAILED: 'Could not categorize the transactions',
  SEGMENT_LOAD_ENRICHMENT_FAILED: 'Could not load the categorized transactions',
  SEGMENT_BATCH_PROCESSING_ERROR: 'The categorization batch hit an error',
  SEGMENT_REFINING_FAILED: 'Could not refine the categorized transactions',
  SEGMENT_ENRICHMENT_FAILED_RETRY_LIMIT:
    'Categorization failed after several tries',
}

export const formatAccountType = (value: string | null | undefined): string => {
  const raw = (value ?? '').trim()
  if (!raw) return ''
  return ACCOUNT_TYPE_LABELS[raw.toLowerCase()] ?? humanizeText(raw)
}

export const formatDocumentClass = (
  value: string | null | undefined,
): string => {
  const raw = (value ?? '').trim()
  if (!raw) return 'Document'
  return DOCUMENT_CLASS_LABELS[raw.toLowerCase()] ?? humanizeText(raw)
}

// The wire value is a hash like {code: 'SEGMENT_ENRICHMENT_FAILED'}; a bare string is a legacy shape
// we still accept defensively.
export const formatFailureReason = (
  value: SegmentFailureReason | string | null | undefined,
): string => {
  const code = typeof value === 'string' ? value : (value?.code ?? '')
  const raw = code.trim()
  if (!raw) return ''
  return FAILURE_REASON_LABELS[raw] ?? humanizeText(raw)
}

export const formatActivityType = (
  value: string | null | undefined,
): string => {
  const raw = (value ?? '').trim()
  if (!raw) return ''
  return ACTIVITY_TYPE_LABELS[raw.toLowerCase()] ?? humanizeText(raw)
}

export const formatAssetClass = (value: string | null | undefined): string => {
  const raw = (value ?? '').trim()
  if (!raw) return 'Unclassified'
  return ASSET_CLASS_LABELS[raw.toLowerCase()] ?? humanizeText(raw)
}
